import {
  DepositAccountResultPageV2,
  type MonetaryAmount,
} from '@resurs-internal/deposits-app-components';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';

import { type ApplicationHandlerProps } from '@/components';
import { CountryContext } from '@/contexts';
import {
  getSectionCount,
  hasFixedTermView,
  isAuthenticated,
  isDevelopment,
  isIntegration,
  sendGTMApplicationProgress,
  sendGTMApplicationStatus,
  sendGTMApplicationSubmitted,
} from '@/helpers';
import { useApplication } from '@/hooks';
import { type GTMStatus } from '@/models';
import { depositFunds, pollForAccountInfo } from '@/services';

export const DepositAccountViewV2 = () => {
  const { applicationId, product } =
    useOutletContext() as ApplicationHandlerProps;
  const navigate = useNavigate();
  const { application } = useApplication(applicationId);
  const { country } = useContext(CountryContext);
  const { t } = useTranslation();

  const section =
    getSectionCount(
      country,
      application.productType,
      hasFixedTermView(product),
    ) - 1;

  const handleError = () => {
    navigate('/error');
  };
  const handleGTMSubmitted = (
    applicationId: string,
    productVariant: string,
    stepName: string,
    stepNumber: number,
  ) => {
    sendGTMApplicationSubmitted({
      applicationId,
      country,
      productVariant,
      stepName,
      stepNumber,
    });
  };
  const handleGTMStatus = (
    applicationId: string,
    productVariant: string,
    stepName: string,
    status: string,
  ) => {
    sendGTMApplicationStatus({
      applicationId,
      country,
      productVariant,
      status: status as GTMStatus,
      stepName,
    });
  };

  const handleGTMProgress = (
    applicationId: string,
    productVariant: string,
    stepName: string,
    stepNumber: number,
  ) => {
    sendGTMApplicationProgress({
      applicationId,
      country,
      productVariant,
      stepName,
      stepNumber,
    });
  };

  const handleDepositFunds = async (
    amount: MonetaryAmount,
    transactionText?: string,
  ) => {
    await depositFunds(
      applicationId,
      `${window.location.origin}/application/${applicationId}/funds`,
      amount,
      transactionText,
    )
      .then((response) => {
        handleGTMProgress(
          applicationId,
          product?.name,
          t('category.add-funds.title'),
          section,
        );
        if (response && response._links?.['openbanking-redirect']?.href) {
          window.location.href = response._links['openbanking-redirect'].href;
        }
      })
      .catch(() => {
        handleError();
      });
  };

  return (
    <DepositAccountResultPageV2
      applicationId={applicationId}
      country={country}
      depositFunds={handleDepositFunds}
      isAuthenticated={isAuthenticated}
      isDevelopment={isDevelopment}
      isIntegration={isIntegration}
      navigateError={handleError}
      pollForAccountInfo={pollForAccountInfo}
      product={product}
      section={section}
      sendGTMApplicationStatus={handleGTMStatus}
      sendGTMApplicationSubmitted={handleGTMSubmitted}
    />
  );
};
